import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAI } from '../context/AIContext.jsx';
import { DOMAIN_WORKFLOWS } from '../config/domainWorkflows.ts';
import { Bot, Check, Clock3, Copy, Play, Settings, BarChart3 } from 'lucide-react';

const T = {
  primary: 'var(--lyra-color-primary)',
  accentSoft: 'var(--app-primary-soft)',
  accentMid: '#5dcaa5',
  violetSoft: 'var(--app-primary-soft)',
  violet: 'var(--lyra-color-primary)',
  amberSoft: 'var(--lyra-color-warning-bg)',
  border: '0.5px solid #d9ddd9',
  radius: 12,
  text: 'var(--lyra-color-text)',
  muted: '#647067',
};

type HistoryItem = {
  id: string;
  capability: string;
  status: 'approved' | 'pending';
  ts: string;
  summary: string;
};

function buildMockOutput(title: string, values: Record<string, string>) {
  return {
    title,
    values,
    recommendations: [
      'Prioritize high-confidence items first',
      'Route uncertain items for human review',
      'Export only approved output to downstream systems',
    ],
  };
}

export default function DomainWorkflowPage() {
  const { domainId = '' } = useParams();
  const config = DOMAIN_WORKFLOWS[domainId];
  const { getEnabledModels, selectedModelId } = useAI();
  const models = getEnabledModels ? getEnabledModels() : [];

  const [activeCardId, setActiveCardId] = useState(config?.capabilityCards?.[0]?.id || '');
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [output, setOutput] = useState<any>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [modelId, setModelId] = useState<string>(selectedModelId || 'auto');

  const activeCard = useMemo(() => config?.capabilityCards.find(c => c.id === activeCardId), [config, activeCardId]);

  if (!config) {
    return (
      <div style={{ padding: 20, color: T.text }}>
        <div style={{ fontSize: 18, fontWeight: 500 }}>Domain not configured</div>
      </div>
    );
  }

  const runFlow = async () => {
    if (!activeCard) return;
    setIsRunning(true);
    await new Promise((resolve) => setTimeout(resolve, 700));
    const produced = buildMockOutput(activeCard.title, inputValues);
    setOutput(produced);
    setHistory((prev) => [
      {
        id: String(Date.now()),
        capability: activeCard.title,
        status: 'pending',
        ts: new Date().toISOString(),
        summary: `Generated ${activeCard.outputs.length} structured outputs`,
      },
      ...prev,
    ]);
    setIsRunning(false);
  };

  const approveAndExport = () => {
    if (!output || !activeCard) return;
    setHistory((prev) => prev.map((item, idx) => idx === 0 ? { ...item, status: 'approved', summary: `${item.summary}; approved + exported` } : item));
  };

  return (
    <div style={{ padding: 18, color: T.text }}>
      <div style={{ border: T.border, borderRadius: T.radius, background: 'var(--lyra-color-surface)', padding: 12, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: T.accentSoft, color: T.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Bot size={16} /></div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{config.title} Agent Runtime</div>
            <div style={{ fontSize: 12, color: T.muted }}>{config.subtitle}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select value={modelId} onChange={(e) => setModelId(e.target.value)} style={{ border: T.border, borderRadius: 10, height: 32, padding: '0 10px', fontSize: 12, fontWeight: 400 }}>
            <option value="auto">Auto model</option>
            {models.map((m: any) => <option key={m.id} value={m.id}>{m.name || m.id}</option>)}
          </select>
          <span style={{ border: T.border, borderRadius: 999, padding: '5px 10px', background: T.accentSoft, fontSize: 11, fontWeight: 500, color: T.primary }}>Context</span>
          <span style={{ border: T.border, borderRadius: 999, padding: '5px 10px', background: T.violetSoft, fontSize: 11, fontWeight: 500, color: T.violet }}>Usage</span>
          <span style={{ border: T.border, borderRadius: 999, padding: '5px 10px', background: T.amberSoft, fontSize: 11, fontWeight: 500, color: '#8a6f2c' }}>Runtime</span>
        </div>
      </div>

      <div style={{ border: T.border, borderRadius: T.radius, background: 'var(--lyra-color-surface)', padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 10 }}>1. Sub-capabilities</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 10 }}>
          {config.capabilityCards.map((card) => {
            const Icon = card.icon;
            const active = card.id === activeCardId;
            return (
              <button key={card.id} onClick={() => setActiveCardId(card.id)} style={{ textAlign: 'left', border: T.border, borderRadius: T.radius, padding: 12, background: active ? T.accentSoft : 'var(--lyra-color-surface)', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 24, height: 24, borderRadius: 7, background: 'var(--lyra-color-surface)', border: T.border, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={14} color={T.primary} /></div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{card.title}</div>
                </div>
                <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.5 }}>{card.description}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ border: T.border, borderRadius: T.radius, background: 'var(--lyra-color-surface)', padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 10 }}>2. Run Flow</div>
        {activeCard && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10, marginBottom: 10 }}>
              {activeCard.inputs.map((field) => (
                <label key={field} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: T.muted }}>{field}</span>
                  <input
                    value={inputValues[field] || ''}
                    onChange={(e) => setInputValues((prev) => ({ ...prev, [field]: e.target.value }))}
                    style={{ border: T.border, borderRadius: 10, height: 36, padding: '0 10px', fontSize: 12, fontWeight: 400 }}
                    placeholder={`Enter ${field.toLowerCase()}`}
                  />
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <button onClick={runFlow} disabled={isRunning} style={{ height: 34, border: 'none', borderRadius: 10, background: T.primary, color: 'var(--app-on-accent)', padding: '0 12px', fontSize: 12, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <Play size={14} /> {isRunning ? 'Running...' : 'Run agent'}
              </button>
              <button onClick={approveAndExport} disabled={!output} style={{ height: 34, border: T.border, borderRadius: 10, background: 'var(--lyra-color-surface)', color: T.text, padding: '0 12px', fontSize: 12, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <Check size={14} /> Approve + Export
              </button>
            </div>

            <div style={{ border: T.border, borderRadius: T.radius, background: 'var(--lyra-color-surface)', padding: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}><Settings size={14} /> Structured output</div>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12, color: T.text, fontWeight: 400 }}>{output ? JSON.stringify(output, null, 2) : 'Run a capability to generate structured output.'}</pre>
            </div>
          </>
        )}
      </div>

      <div style={{ border: T.border, borderRadius: T.radius, background: 'var(--lyra-color-surface)', padding: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}><BarChart3 size={16} /> 3. History / Audit Log</div>
        {history.length === 0 ? (
          <div style={{ fontSize: 12, color: T.muted, fontWeight: 400 }}>No runs yet. Execute a capability to populate audit history.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {history.map((entry) => (
              <div key={entry.id} style={{ border: T.border, borderRadius: 10, padding: 10, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{entry.capability}</div>
                  <div style={{ fontSize: 12, color: T.muted, fontWeight: 400 }}>{entry.summary}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ border: T.border, borderRadius: 999, padding: '4px 9px', fontSize: 11, fontWeight: 500, background: entry.status === 'approved' ? T.accentSoft : T.violetSoft, color: entry.status === 'approved' ? T.primary : T.violet }}>{entry.status}</span>
                  <span style={{ fontSize: 11, color: T.muted, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock3 size={12} /> {new Date(entry.ts).toLocaleString()}</span>
                  <button style={{ border: T.border, borderRadius: 8, height: 28, padding: '0 8px', fontSize: 11, fontWeight: 500, background: 'var(--lyra-color-surface)', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Copy size={12} /> Copy</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
